import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import style from "./component.module.sass";
import { useParams } from "react-router-dom";
import { TreeItem, SortableTreeWithoutDndContext as SortableTree } from "react-sortable-tree";
import FileExplorerTheme from "react-sortable-tree-theme-file-explorer";
import "react-sortable-tree/style.css"; // This only needs to be imported once in your app
import {

  addRemoveOnDragComplete,
  updateForms,
} from "../../../../../stateManager/store/formCreation/action";
import { findIdSelector } from "./reduxSelector";
import {
  compareDeep,
  generateId,
  resetLayoutProperties,
  setLayoutProperties,
  generateSectionalIds,
  mergeAssetFieldWithFormField,
} from "../../../../../helpers/commonFunction";
import { PUSH_ACTION_STACK } from "../../../../../stateManager/store/formCreation/types";
import { fCardSelect } from "../../../../../helpers/RxServices";
import search from "../../../../../assets/images/searchicon.png";

const getTreeData = (data: any[], assets: any[], collapsed: string[]): any[] => {
  return data.map((x) => {
    let field = x;
    const aId = field.assetId

    if (aId && assets[aId] && assets[aId].json && field.elementId === assets[aId].json.elementId) {
      const assetCopy = JSON.parse(JSON.stringify(assets[aId].json))
      const fieldCopy = JSON.parse(JSON.stringify(field))
      field = mergeAssetFieldWithFormField(assetCopy, fieldCopy)
    }

    if (field.fields && field.fields.length > 0) {
      const payload: TreeItem = {
        ...field,
        title:
          Object.keys(field.properties).length && field.properties.label
            ? field.properties.label
            : field.name || field.label || "Undefined",
        treeIndex: field.id,
        expanded: !collapsed.includes(field.id),
        children: getTreeData(field.fields, assets, collapsed),
        isDirectory: field.fields.length > 0 ? true : false,
      };
      return payload;
    } else {
      const payload = {
        ...field,
        title:
          Object.keys(field.properties).length && field.properties.label
            ? field.properties.label
            : field.name || field.label || "Undefined",
        isDirectory: false,
        children: [],
      };
      return payload;
    }
  });
};

const getFormData = (data: any[]): any[] => {
  const payload = data.map((x) => {
    const formPayload = {
      ...x,
      name: x.name,
      label: x.name || x.label || "Undefined",
      fields: x.children.length ? getFormData(x.children) : [],
      isDirectory: x.children.length ? true : false,
    };
    return formPayload;
  });
  return payload;
};

const Tree = () => {
  const [collapsed, setCollapsed] = useState<any[]>([])
  const [selectedCard, setSelectedCard] = useState<any>(null);
  const dispatch = useDispatch();
  const routeParams: any = useParams();
  const [selectedNode, setSelectedNode] = useState<any>({
    id: "",
    fields: [],
  });
  const [selectedNodeNextParent, setSelectedNodeNextParent] = useState<any>({});
  const [searchString, setSearchString] = useState("");
  const [searchFocusIndex, setSearchFocusIndex] = useState(-1);
  const reduxState = useSelector((state: any) => state);
  const reduxFormcreation = reduxState.formcreation;
  const assets = reduxState.assets.list;
  const [state, setState] = useState<TreeItem[]>(
    getTreeData(reduxFormcreation[routeParams.formId], assets, collapsed)
  );

  fCardSelect.getMessage().subscribe((res) => {
    if (res.id) {
      setSelectedCard(res);
    } else {
      setSelectedCard(null);
    }
  });

  useEffect(() => {
    const data = reduxFormcreation[routeParams.formId];
    const dataToSet = getTreeData(data, assets, collapsed);
    setState(dataToSet);
  }, [reduxFormcreation, routeParams.formId, collapsed, assets]);

  const cardContainer = useSelector((state) => {
    const cardIdSplit = selectedNode.id.split("#");
    cardIdSplit.pop();
    if (selectedNode.id) {
      const val = findIdSelector(state, routeParams.formId, cardIdSplit, true);
      return val;
    } else {
      return {};
    }
  }, compareDeep);

  useEffect(() => {
    if (selectedNode.id && Object.keys(selectedNodeNextParent).length > 0) {
      const selNodeCopy = JSON.parse(JSON.stringify(selectedNode))

      if (selectedNodeNextParent.type === "Section") {
        resetLayoutProperties(selectedNodeNextParent, selNodeCopy, selectedNode)
      }

      if (selectedNodeNextParent.type === "Container") {
        setLayoutProperties(selectedNodeNextParent, selectedNode)
      }

      const deleteData: any = {
        formid: routeParams.formId,
        draggedItemContainer: JSON.parse(JSON.stringify(cardContainer)),
        itemId: selectedNode.id,
      };
      const undoDeleteData: any = {
        formid: routeParams.formId,
        droppedContainer: { json: JSON.parse(JSON.stringify(cardContainer)) },
        draggedItem: JSON.parse(JSON.stringify(selectedNode)),
      };

      let newItem = null;
      const newId: string = generateId(
        { json: selectedNode },
        { json: selectedNodeNextParent }
      );

      if (selectedNode.fields && selectedNode.fields.length > 0) {
        newItem = generateSectionalIds({ json: selectedNode }, newId);
      } else {
        newItem = JSON.parse(JSON.stringify(selectedNode));
        newItem.id = newId;
      }
      const addData: any = {
        formid: routeParams.formId,
        draggedItem: newItem,
        droppedContainer: { json: selectedNodeNextParent },
      };
      const undoAddData: any = {
        formid: routeParams.formId,
        itemId: newId,
        draggedItemContainer: selectedNodeNextParent,
      };
      dispatch({
        type: PUSH_ACTION_STACK,
        data: {
          undo: () => {
            dispatch(
              addRemoveOnDragComplete(undoAddData, undoDeleteData, true)
            );
          },
          redo: () => {
            dispatch(addRemoveOnDragComplete(deleteData, addData));
          },
        },
      });

      dispatch(addRemoveOnDragComplete(deleteData, addData));
      setSelectedNode({ id: "", fields: [] });
      setSelectedNodeNextParent({});
      setCollapsed(prevState => prevState.map(el => el === selectedNode.id ? newId : el))
    }
  }, [cardContainer, selectedNode, selectedNodeNextParent, routeParams.formId, dispatch]);

  const handleTreeUpdate = async (
    node: any,
    nextParentNode: any,
    treeData: any
  ) => {
    const idArray = node.id.split("#");
    idArray.pop();
    if (idArray.join("#") !== nextParentNode.id) {
      setSelectedNode(node);
      setSelectedNodeNextParent(nextParentNode);
    } else {
      const formData = getFormData(treeData);
      dispatch(
        updateForms({
          id: routeParams.formId,
          data: formData,
        })
      );
    }
  };

  const onCardClick = (rowProps: any) => {
    const { node } = rowProps;
    const selectedSection = node.id.split("#").slice(0, 2).join("#");
    fCardSelect.sendMessage({ id: node.id, json: { ...node, selectedSection } });
  }

  return (
    <>
      <div style={{ height: "84.5vh", padding: "12px 0 12px 16px " }}>
        <div className={style.search + " row"}>
          <img src={search} alt="" />
          <input data-test-id="component_search" type="search" placeholder="Search here..."
            value={searchString}
            onChange={(e) => setSearchString(e.target.value)} />
        </div>
        <SortableTree
          theme={FileExplorerTheme}
          innerStyle={{
            fontSize: "16px",
          }}
          generateNodeProps={(rowProps: any) => ({
            onClick: () => onCardClick(rowProps),
            style: {
              color:
                selectedCard && rowProps.node.id === selectedCard.id
                  ? "#0076ff"
                  : "black",
            },
            className: rowProps.node.isDirectory ? "parent" : "child"
          })}
          rowHeight={28}
          treeData={state}
          onChange={(treeData) => setState(treeData)}
          searchQuery={searchString}
          searchFocusOffset={searchFocusIndex}
          onMoveNode={({ node, nextParentNode, treeData }) =>
            handleTreeUpdate(node, nextParentNode, treeData)
          }
          canDrag={(e: any) => {
            // should not allow reorder of asset elements
            if (e.node && e.node["isChildAsset"]) {
              return false
            }
            return true
          }}
          canDrop={(e: any) => {
            // should not allow form elements gets dropped into an asset
            if (e.nextParent && e.nextParent.isAsset) {
              return false
            } else if (
              e.node &&
              (e.node.type === "Section" || e.node.type === "Container") &&
              e.nextParent &&
              e.nextParent.type === "TabBar"
            ) {
              return true;
            } else if (e.nextParent && (e.nextParent.type === "Section" || e.nextParent.type === "Container")) {
              return true;
            }
            return false;
          }}
          canNodeHaveChildren={(e: any) => e.type === "Section" || e.type === "Container" || e.type === "TabBar" || e.type === 'FormWizard'}
          searchFinishCallback={(matches) => {
            setSearchFocusIndex(
              matches.length > 0 ? searchFocusIndex % matches.length : 0
            );
          }}
          onVisibilityToggle={(data: any) => {
            const id = data.node.id
            if (!data.expanded && !collapsed.includes(id)) {
              setCollapsed(prevState => [...prevState, id])
            } else if (data.expanded) {
              setCollapsed(prevState => prevState.filter(el => el !== id))
            }
          }}
        />
      </div>
    </>
  );
};

export default Tree;
